import { LotConfigStep } from "src/routes/developments/lot-summary/lot-release/enums";
import { ReassignPurchaseOrdersStep } from "src/routes/developments/reassign-purchase-orders/enums";
import {
  costMappingPath,
  developmentCommitmentsPath,
  developmentCommitmentsPaths,
  developmentPath,
  developmentPaths,
  developmentsPath,
  reassignPurchaseOrdersPath,
  reassignPurchaseOrdersPaths,
} from "src/routing/domains/developmentsPaths";
import { billsRoute, invoicesRoute } from "src/routing/domains/financesPaths";
import { personalDashboardPaths } from "src/routing/domains/personalDashboardPaths";
import { projectReleaseToTradesRoute, suggestableProjectDetailPaths } from "src/routing/domains/projectsPaths";
import { expensesRoute, searchRoute } from "src/routing/domains/rootPaths";
import { tradePartnersPath } from "src/routing/domains/tradePartnersPaths";
import {
  tradesBidContractsRoute,
  tradesPurchaseOrdersRoute,
  tradesRoute,
  tradesTradePartnersRoute,
} from "src/routing/domains/tradesPaths";
import { StageUrlSlug } from "src/utils";

// Re-export development paths from the data-router domain (compat for existing importers).
export {
  costMappingPath,
  developmentCommitmentsPath,
  developmentCommitmentsPaths,
  developmentPath,
  developmentPaths,
  developmentsPath,
  reassignPurchaseOrdersPath,
  reassignPurchaseOrdersPaths,
};

// Re-export for suggestablePaths / global nav; path constants live in tradePartnersPaths.ts.
export { tradePartnersPath };

// Re-export for suggestablePaths / global nav; path constants live in personalDashboardPaths.ts.
export { personalDashboardPaths };

// Re-export for suggestablePaths / existing importers; path constants live in rootPaths.ts.
export const expensesPath = expensesRoute.path;
export const searchPath = searchRoute.path;

export type ProjectParams = { projectId: string };
export type BidItemParams = { bidItemId: string };
export type MaterialParams = { listingId: string; variantId: string };
export type StageParams = { stageSlug: StageUrlSlug };
export type ProjectStageParams = ProjectParams & StageParams;
export type IdOrAddParams = { idOrAdd: string };
export type DevelopmentCommitmentParams = { developmentCommitmentId: string };
export type ChangeEventParams = { changeEventId: string };
export type GuidedChangeEventParams = ProjectParams & IdOrAddParams;
export type DevelopmentParams = { developmentId: string };
export type DevelopmentContractParams = { developmentId: string; bidContractRevisionId: string };
export type ItemTemplateItemParams = { itemTemplateId: string; itemTemplateItemId?: string };
export type ScheduleTemplateParams = { scheduleTemplateId: string };
export type LotConfigParams = { projectId?: string; currentStep: LotConfigStep };
export type PlanDetailsParams = DevelopmentParams & { readyPlanId: string };
export type PlanPackageDetailsParams = { planPackageId: string; planPackageVersionId: string };
export type PlanPackageTakeoffParams = PlanPackageDetailsParams & { rpavId: string };
export type DesignCatalogParams = { designPackageId: string; designPackageVersionId: string };
export type ProductOfferingParams = DevelopmentParams & { productOfferingId: string; rpavId?: string };
export type ProductOfferingVersionParams = { productOfferingId: string; rpavId: string };
export type ProductOfferingScopeParams = ProductOfferingVersionParams;
export type ProductOfferingCostCalculatorParams = ProductOfferingVersionParams;
export type ProductOfferingConfigParams = { productOfferingId: string; productOfferingConfigId: string };
export type ProductOfferingConfigApplyParams = { configId: string; projectId: string };
export type BidPackageParams = DevelopmentParams & { bidPackageGroupId: string; bidPackageId: string };
export type ReassignPurchaseOrdersParams = DevelopmentParams & { currentStep: ReassignPurchaseOrdersStep };
export type WarrantyParams = { warrantyTicketId: string };
/** I.e. the `/libraries/design-packages/rp:2/detail/tli:3/tli:4. */
export type DesignPackageParams = { designPackageId: string; placeholderTliId: string; productTliId?: string };
export type LaborCostParams = { globalPlanTaskId: string };

export const idOrAdd = ":idOrAdd";

// Finances, trade-partners, bid-packages, and trades paths have migrated to the data router; their
// path constants + typed URL builders now live in `src/routing/domains/financesPaths.ts`,
// `tradePartnersPaths.ts`, `bidPackagesPaths.ts`, and `tradesPaths.ts`.

export const tradesPath = tradesRoute.path;
export const tradesPurchaseOrdersPath = tradesPurchaseOrdersRoute.path;
export const tradesTradePartnersPath = tradesTradePartnersRoute.path;
export const tradesBidContractsPath = tradesBidContractsRoute.path;

const documentsPath = "/documents";

const adminPath = "/admin";
export const adminPaths = {
  admin: adminPath,
  projects: `${adminPath}/projects`,
  users: `${adminPath}/users`,
  items: `${adminPath}/items`,
  costCodes: `${adminPath}/costCodes`,
  featureFlags: `${adminPath}/feature-flags`,
  userGroups: `${adminPath}/user-groups`,
  locations: `${adminPath}/locations`,
  featureTypes: `${adminPath}/feature-types`,
  infrastructure: `${adminPath}/infrastructure`,
};

export const projectsPath = "/projects";

export const librariesPath = `/libraries`;

export const globalOptionsPath = `${librariesPath}/global-options`;
export const globalOptionPath = `${globalOptionsPath}/${idOrAdd}`;
export const globalOptionsLegacyPath = `${librariesPath}/global-options-legacy`;
export const materialsCatalogPath = `${librariesPath}/material-catalog`;
export const materialCreatePath = `${materialsCatalogPath}/add`;
export const materialMergePath = `${materialsCatalogPath}/merge`;
export const materialDetailsPath = `${materialsCatalogPath}/:listingId`;
export const materialCostPath = `${materialsCatalogPath}/:variantId/cost`;
export const installTasksPath = `${materialsCatalogPath}/install-tasks`;
export const itemCatalogPath = `${librariesPath}/item-catalog`;
export const bidItemCreatePath = `${itemCatalogPath}/add`;
const templatesPath = `/templates`;
export const scheduleTemplatesPath = `${librariesPath}${templatesPath}/schedule-templates`;
export const scheduleTemplatePath = { scheduleTemplate: `${scheduleTemplatesPath}/:scheduleTemplateId` };

export const catalogTasksPath = `${librariesPath}/task-catalog`;
export const catalogTaskPath = `${catalogTasksPath}/${idOrAdd}`;
export const constraintItemsCatalogPath = `${catalogTasksPath}/constraint-and-allowance-catalog`;
export const catalogTaskFolderPath = `${catalogTasksPath}/:folderId`;
export const catalogTaskLaborCosts = `${catalogTasksPath}/:globalPlanTaskId/labor-costs`;

export const milestonesCatalogPath = `${librariesPath}/milestone-catalog`;
export const milestoneCatalogPath = `${milestonesCatalogPath}/${idOrAdd}`;

export const designCatalogsPath = `${librariesPath}/design-packages`;

// designCatalog inherits from ReadyPlan so its ID per joist-config is `rp:123` not the typical `dc:123`
export const designCatalogPath = `${designCatalogsPath}/:designPackageId/:designPackageVersionId`;
export const designPackageFinishSchedulePath = `${designCatalogPath}/finish-schedule`;
export const designPackageCompareModePath = `${designCatalogPath}/compare-mode`;
export const designPackageProductCardPath = `${designCatalogPath}/detail/:placeholderTliId/:productTliId?`;
export const designPackageProductSearchPath = `${designCatalogPath}/search/:placeholderTliId/:productTliId?`;
export const designPackageAddProductPath = `${designCatalogPath}/add-product/:placeholderTliId/:productTliId?`;
export const designPackageVersionHistoryPath = `${designCatalogPath}/history`;
export const designPackagePersonalizationGroupsPath = `${designCatalogPath}/personalization-groups`;
export const designPackageEditOptionPath = `${designCatalogPath}/options/${idOrAdd}`;
export const designPackageRequiredOptionsPath = `${designCatalogPath}/required-options`;

export const planPackagesPath = `${librariesPath}/plan`;
export const planPackagePath = `${planPackagesPath}/:planPackageId/:planPackageVersionId`;

// `/libraries/*` routes are served by the data router (`src/routing/domains/libraries/`).
// Path constants + typed URL builders live in `src/routing/domains/librariesPaths.ts` (and subtree `*Paths.ts`).
// Many string constants below remain for non-RouteUrls callers; prefer `librariesPaths` / `create*Url` for new code.
// Global compare still uses this path constant until that page is registered under the configs tree.
export const productOfferingConfigsComparePath = `${librariesPath}/product-offering-configs/compare`;

export const projectItemScopeOfWorkPath = `/pdf/projectItem`;
export const tradeScopeOfWorkPath = `/pdf/changeEventLineItem`;
export const scheduleOfValuesPath = `/pdf/schedule-of-values`;
export const homeownerChangeOrderPath = `/pdf/homeowner-change-order`;
export const estimateExportPath = `/pdf/estimate`;
export const scheduleExportPath = `/pdf/schedule/:scheduleId`;
export const tradeSpecificAgreementPath = `/pdf/trade-specific-agreement`;
export const developmentCommitmentPdfPath = `/pdf/development-commitment`;
export const commitmentPdfPath = `/pdf/commitment`;
export const invoicePandaDocPdfPath = `/pdf/invoicePandaDoc`;
export const invoicePdfPath = `/pdf/invoice`;
export const lienWaiverPdfPath = `/pdf/lien-waivers`;
export const toDoPdfPath = `/pdf/to-dos`;
export const commitmentChangeOrderPdfPath = `/pdf/commitment-change-order`;
export const developmentContractPdfPath = `/pdf/development-contract`;
export const bidCommitmentPdfPath = `/pdf/bid-commitment`;
export const bidCommitmentChangeOrderPdfPath = `/pdf/bid-commitment-change-order`;
export const lotSequenceSheetPdfPath = `/pdf/lot-sequence-sheet`;
export const lotSummaryDetailsPdfPath = `/pdf/lot-summary-details`;
export const billPdfPath = `/pdf/bill`;
export const finishScheduleVisualModePdfPath = `/pdf/finish-schedule-visual-mode`;
export const projectFinishScheduleVisualModePdfPath = `/pdf/project-finish-schedule-visual-mode`;
export const projectFinishSchedulePdfPath = `/pdf/project-finish-schedule`;
export const tradesPunchlistPdfPath = `/pdf/trades-punchlist`;

export const releaseToTradesPaths: Record<LotConfigStep, string> = {
  [LotConfigStep.CONFIRM]: `${projectReleaseToTradesRoute.path}/${LotConfigStep.CONFIRM}`,
  [LotConfigStep.REVIEW]: `${projectReleaseToTradesRoute.path}/${LotConfigStep.REVIEW}`,
};

export const suggestablePaths = [
  billsRoute.path,
  developmentCommitmentsPath,
  documentsPath,
  invoicesRoute.path,
  expensesPath,
  personalDashboardPaths.dashboard,
  personalDashboardPaths.toDos,
  projectsPath,
  ...suggestableProjectDetailPaths,
  tradePartnersPath,
];
